/**
 * Ventas → Facturas pagadas por adelantado (`/logistica/pagos-adelantados-documentos`).
 *
 * Puerto de la pantalla `Sales::paid_in_advance` del panel anterior
 * (`views/sales/paid_in_advance.php` + `src/sales/paidInAdvance.js`): el listado de
 * facturas pagadas por adelantado con su **documento de pago** (subir, reemplazar, ver,
 * descargar y eliminar) y el detalle de la factura global (cantidad comprada, entregada y
 * pendiente) con las entregas hechas por notas de venta.
 *
 * - El listado son las ventas administradas con `comprobante_venta = 'true'` y
 *   `paidInAdvance = 1`, con su cliente, su comprobante y el documento cargado.
 * - El documento se guarda en `uploads/pagos_adelantados/` (pdf, png, jpg, jpeg o webp,
 *   hasta 5 MB) y su nombre queda en `admin_venta.paidInAdvanceDocument`; al reemplazarlo
 *   se borra el archivo anterior, como en el panel anterior.
 * - No confundir con **Pagos Adelantados** (`/logistica/pagos-adelantados`), que es el
 *   módulo nuevo de prepagos (`Prepaid::ventas`): esta pantalla es la de los *documentos
 *   de pago* de las facturas antiguas.
 */

import {
  ArrowLeft,
  Download,
  Eye,
  FileText,
  Info,
  ListTree,
  RefreshCw,
  Search,
  Trash2,
  Upload,
} from 'lucide-react';
import { useRef, useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Link } from 'react-router-dom';

import { Alert } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { CampoFecha } from '@/components/ui/calendario';
import { Paginador } from '@/components/ui/paginador';
import { Dialog } from '@/components/ui/dialog';
import { Skeleton } from '@/components/ui/skeleton';
import { useDocumentTitle } from '@/hooks/use-document-title';
import { usePaginacion } from '@/hooks/use-paginacion';
import { abrirPdfAutenticado } from '@/lib/descargas';
import { ApiError } from '@/lib/http/api-error';
import { cn, formatCurrency, formatDateValue, formatNumber } from '@/lib/utils';

import {
  reportesApi,
  useDetallePagoAdelantado,
  usePagosAdelantados,
  type FiltrosPagosAdelantados,
  type PagoAdelantado,
} from '../../reportes/reportes.api';

/** Fecha local `AAAA-MM-DD` (sin desfasar por la zona horaria). */
function fechaLocal(fecha: Date): string {
  return `${fecha.getFullYear()}-${String(fecha.getMonth() + 1).padStart(2, '0')}-${String(
    fecha.getDate(),
  ).padStart(2, '0')}`;
}

/** Rango por defecto: el año en curso (son facturas antiguas). */
function rangoPorDefecto(): FiltrosPagosAdelantados {
  const hoy = new Date();

  return {
    desde: `${hoy.getFullYear()}-01-01`,
    hasta: fechaLocal(hoy),
    busqueda: '',
    sin_documento: false,
  };
}

export function PagosAdelantadosDocumentosPage() {
  useDocumentTitle('Ventas · Pagos adelantados (documentos)');

  const [filtros, setFiltros] = useState<FiltrosPagosAdelantados>(rangoPorDefecto);
  const [detalleDe, setDetalleDe] = useState<number | null>(null);
  const [aviso, setAviso] = useState<string | null>(null);
  const [exito, setExito] = useState<string | null>(null);

  const archivo = useRef<HTMLInputElement>(null);
  const objetivo = useRef<number | null>(null);
  const cliente = useQueryClient();

  const consulta = usePagosAdelantados(filtros);
  const datos = consulta.data;
  const resumen = datos?.resumen;
  const ventas = datos?.ventas ?? [];
  // La tabla se pagina en el navegador; los contadores y el pie siguen usando el listado
  // completo.
  const paginacion = usePaginacion(ventas, { clave: filtros });

  const detalle = useDetallePagoAdelantado(detalleDe);
  const ventaDelDetalle = ventas.find((venta) => venta.id === detalleDe) ?? null;

  const subida = useMutation({
    mutationFn: ({ venta, file }: { venta: number; file: File }) =>
      reportesApi.subirDocumentoPago(venta, file),
    onSuccess: (respuesta) => {
      setAviso(null);
      setExito(respuesta.mensaje);
      void cliente.invalidateQueries({ queryKey: ['reportes', 'pagos-adelantados-documentos'] });
    },
    onError: (fallo) => {
      setExito(null);
      setAviso(fallo instanceof Error ? fallo.message : 'No se pudo subir el documento.');
    },
  });

  const borrado = useMutation({
    mutationFn: (venta: number) => reportesApi.eliminarDocumentoPago(venta),
    onSuccess: (respuesta) => {
      setAviso(null);
      setExito(respuesta.mensaje);
      void cliente.invalidateQueries({ queryKey: ['reportes', 'pagos-adelantados-documentos'] });
    },
    onError: (fallo) => {
      setExito(null);
      setAviso(fallo instanceof Error ? fallo.message : 'No se pudo eliminar el documento.');
    },
  });

  const cambiar = <C extends keyof FiltrosPagosAdelantados>(
    campo: C,
    valor: FiltrosPagosAdelantados[C],
  ) => setFiltros((previos) => ({ ...previos, [campo]: valor }));

  /** Abre el selector de archivos para la factura elegida. */
  const elegirArchivo = (venta: number) => {
    objetivo.current = venta;
    setAviso(null);
    setExito(null);
    archivo.current?.click();
  };

  const subirArchivo = (file: File | undefined) => {
    if (!file || objetivo.current === null) return;

    subida.mutate({ venta: objetivo.current, file });
    objetivo.current = null;

    if (archivo.current) archivo.current.value = '';
  };

  const verDocumento = async (venta: PagoAdelantado) => {
    setAviso(null);

    try {
      await abrirPdfAutenticado(reportesApi.urlDocumentoPago(venta.id));
    } catch (fallo) {
      setAviso(fallo instanceof Error ? fallo.message : 'No se pudo abrir el documento.');
    }
  };

  const descargarDocumento = async (venta: PagoAdelantado) => {
    setAviso(null);

    try {
      await abrirPdfAutenticado(reportesApi.urlDocumentoPago(venta.id), {
        nombre: venta.documento ?? `pago-adelantado-${venta.id}.pdf`,
      });
    } catch (fallo) {
      setAviso(fallo instanceof Error ? fallo.message : 'No se pudo descargar el documento.');
    }
  };

  const eliminarDocumento = (venta: PagoAdelantado) => {
    const confirmado = window.confirm(
      `¿Eliminar el documento de pago de la factura ${venta.comprobante}? Se borrará el archivo del servidor.`,
    );

    if (confirmado) borrado.mutate(venta.id);
  };

  return (
    <div className="space-y-5">
      <header className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <Link
            to="/logistica/pagos-adelantados"
            className="inline-flex items-center gap-1.5 text-xs font-semibold text-brand-700 hover:underline"
          >
            <ArrowLeft className="size-3.5" aria-hidden="true" />
            Pagos adelantados
          </Link>
          <h1 className="mt-1 text-2xl font-bold text-ink-900">
            Documentos de pago por adelantado
          </h1>
          <p className="mt-1 text-sm text-ink-500">
            Las facturas pagadas por adelantado y su documento de pago: súbelo, reemplázalo o
            elimínalo, y consulta el detalle de entregas.
          </p>
        </div>

        <Button
          variant="outline"
          onClick={() => void consulta.refetch()}
          leftIcon={<RefreshCw className="size-4" aria-hidden="true" />}
        >
          Actualizar
        </Button>
      </header>

      <input
        ref={archivo}
        type="file"
        accept=".pdf,.png,.jpg,.jpeg,.webp"
        className="hidden"
        onChange={(evento) => subirArchivo(evento.target.files?.[0])}
      />

      <Card>
        <CardContent className="space-y-4 p-4">
          <div className="grid items-end gap-3 sm:grid-cols-2 xl:grid-cols-4">
            <label className="flex flex-col gap-1.5 text-sm">
              <span className="font-medium text-ink-700">Desde</span>
              <span className="flex h-11 items-center rounded-control border border-ink-200 bg-white focus-within:border-brand-400 focus-within:ring-4 focus-within:ring-brand-100">
                <CampoFecha
                  tipo="date"
                  value={filtros.desde}
                  onChange={(evento) => cambiar('desde', evento.target.value)}
                  className="w-full px-3 text-sm"
                />
              </span>
            </label>

            <label className="flex flex-col gap-1.5 text-sm">
              <span className="font-medium text-ink-700">Hasta</span>
              <span className="flex h-11 items-center rounded-control border border-ink-200 bg-white focus-within:border-brand-400 focus-within:ring-4 focus-within:ring-brand-100">
                <CampoFecha
                  tipo="date"
                  value={filtros.hasta}
                  onChange={(evento) => cambiar('hasta', evento.target.value)}
                  className="w-full px-3 text-sm"
                />
              </span>
            </label>

            <label className="flex flex-col gap-1.5 text-sm xl:col-span-2">
              <span className="font-medium text-ink-700">Buscar</span>
              <span className="flex h-11 items-center gap-2 rounded-control border border-ink-200 bg-white px-3 focus-within:border-brand-400 focus-within:ring-4 focus-within:ring-brand-100">
                <Search className="size-4 text-ink-400" aria-hidden="true" />
                <input
                  type="text"
                  value={filtros.busqueda}
                  onChange={(evento) => cambiar('busqueda', evento.target.value)}
                  placeholder="Comprobante, RUC/DNI o nombre del cliente"
                  className="w-full bg-transparent text-sm text-ink-900 outline-none"
                  aria-label="Buscar"
                />
              </span>
            </label>
          </div>

          <div className="flex flex-wrap items-center gap-4 border-t border-ink-100 pt-3">
            <label className="inline-flex items-center gap-2 text-xs font-semibold text-ink-700">
              <input
                type="checkbox"
                className="size-4 accent-brand-600"
                checked={filtros.sin_documento}
                onChange={(evento) => cambiar('sin_documento', evento.target.checked)}
              />
              Sólo las que no tienen documento de pago
            </label>
          </div>
        </CardContent>
      </Card>

      {consulta.isError ? (
        <Alert variant="error" title="No se pudo cargar el listado">
          {consulta.error instanceof ApiError
            ? consulta.error.message
            : 'Inténtalo de nuevo en unos segundos.'}
        </Alert>
      ) : null}

      {aviso ? (
        <Alert variant="error" title="No se pudo completar la operación">
          {aviso}
        </Alert>
      ) : null}

      {exito ? (
        <Alert variant="success" title="Listo">
          {exito}
        </Alert>
      ) : null}

      <div className="grid grid-cols-2 gap-3 sm:grid-cols-2 xl:grid-cols-4">
        {[
          {
            etiqueta: 'Facturas',
            valor: formatNumber(resumen?.ventas ?? 0),
            detalle: formatCurrency(resumen?.total ?? 0),
          },
          {
            etiqueta: 'Con documento',
            valor: formatNumber(resumen?.con_documento ?? 0),
            detalle: 'documento de pago cargado',
          },
          {
            etiqueta: 'Sin documento',
            valor: formatNumber(resumen?.sin_documento ?? 0),
            detalle: 'pendientes de subir',
          },
          {
            etiqueta: 'Pendiente de entrega',
            valor: formatNumber(resumen?.pendiente_entrega ?? 0),
            detalle: `${formatNumber(resumen?.pendientes ?? 0)} factura(s) con saldo`,
          },
        ].map((tarjeta) => (
          <Card key={tarjeta.etiqueta}>
            <CardContent className="p-4">
              <p className="text-xs font-semibold tracking-wide text-ink-500 uppercase">
                {tarjeta.etiqueta}
              </p>
              <p className="text-lg font-bold text-ink-900">{tarjeta.valor}</p>
              <p className="text-xs text-ink-500">{tarjeta.detalle}</p>
            </CardContent>
          </Card>
        ))}
      </div>

      <Card>
        <CardContent className="space-y-3 p-4">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <h2 className="text-sm font-semibold text-ink-800">
              Facturas pagadas por adelantado ({formatNumber(ventas.length)})
            </h2>
            <span className="text-xs text-ink-500">
              {formatDateValue(filtros.desde)} — {formatDateValue(filtros.hasta)}
            </span>
          </div>

          {consulta.isLoading ? <Skeleton className="h-64 w-full" /> : null}

          {!consulta.isLoading ? (
            <>
            <div className="max-h-[60vh] overflow-y-auto rounded-card border border-ink-100">
              <table className="w-full table-fixed border-collapse text-[13px]">
                <thead className="sticky top-0 z-10 bg-white shadow-[0_1px_0_0_var(--color-ink-100)]">
                  <tr className="text-left text-[11px] font-semibold tracking-wide text-ink-500 uppercase">
                    <th className="w-[5.25rem] px-1.5 py-2">Fecha</th>
                    <th className="w-[6.5rem] px-1.5 py-2"># Identidad</th>
                    <th className="px-1.5 py-2">Nombre comercial</th>
                    <th className="w-[7.5rem] px-1.5 py-2"># Comprobante</th>
                    <th className="w-[6rem] px-1.5 py-2 text-right">Total</th>
                    <th className="px-1.5 py-2">Documento de pago</th>
                    <th className="w-[4.5rem] px-1.5 py-2" />
                  </tr>
                </thead>
                <tbody>
                  {ventas.length === 0 ? (
                    <tr className="border-t border-ink-100">
                      <td colSpan={7} className="px-1.5 py-10 text-center text-sm text-ink-500">
                        No hay facturas pagadas por adelantado con esos filtros.
                      </td>
                    </tr>
                  ) : null}

                  {paginacion.visibles.map((venta) => (
                    <tr key={venta.id} className="border-t border-ink-100 hover:bg-brand-50/30">
                      <td className="px-1.5 py-2 text-ink-700">{formatDateValue(venta.fecha)}</td>
                      <td className="px-1.5 py-2 text-ink-700">{venta.numero_doc ?? '—'}</td>
                      <td className="px-1.5 py-2">
                        <p className="truncate font-semibold text-ink-900" title={venta.cliente ?? ''}>
                          {venta.cliente ?? '—'}
                        </p>
                        <p className="truncate text-[11px] text-ink-500">
                          {formatNumber(venta.comprado)} comprado ·{' '}
                          {formatNumber(venta.entregado)} entregado ·{' '}
                          {formatNumber(venta.pendiente)} pendiente
                        </p>
                      </td>
                      <td className="px-1.5 py-2">
                        <p className="truncate font-semibold text-ink-800">{venta.comprobante}</p>
                        {venta.estado_sunat ? (
                          <p className="truncate text-[11px] text-ink-500">{venta.estado_sunat}</p>
                        ) : null}
                      </td>
                      <td className="px-1.5 py-2 text-right font-semibold text-ink-900">
                        {formatCurrency(venta.total)}
                      </td>
                      <td className="px-1.5 py-2">
                        {venta.documento ? (
                          <div className="flex flex-wrap items-center gap-1.5">
                            <Badge variant="success" className="px-1.5 py-0 text-[10px]">
                              Cargado
                            </Badge>
                            <button
                              type="button"
                              onClick={() => void verDocumento(venta)}
                              className="inline-flex items-center gap-1 text-[11px] font-semibold text-brand-700 hover:underline"
                              title={venta.documento}
                            >
                              <Eye className="size-3.5" aria-hidden="true" />
                              Ver
                            </button>
                            <button
                              type="button"
                              onClick={() => void descargarDocumento(venta)}
                              className="inline-flex items-center gap-1 text-[11px] font-semibold text-ink-600 hover:text-brand-700 hover:underline"
                            >
                              <Download className="size-3.5" aria-hidden="true" />
                              Descargar
                            </button>
                            <button
                              type="button"
                              onClick={() => elegirArchivo(venta.id)}
                              className="inline-flex items-center gap-1 text-[11px] font-semibold text-ink-600 hover:text-brand-700 hover:underline"
                            >
                              <Upload className="size-3.5" aria-hidden="true" />
                              Reemplazar
                            </button>
                            <button
                              type="button"
                              onClick={() => eliminarDocumento(venta)}
                              className="inline-flex items-center gap-1 text-[11px] font-semibold text-rose-700 hover:underline"
                            >
                              <Trash2 className="size-3.5" aria-hidden="true" />
                              Eliminar
                            </button>
                          </div>
                        ) : (
                          <button
                            type="button"
                            onClick={() => elegirArchivo(venta.id)}
                            className="inline-flex items-center gap-1.5 rounded-control border border-amber-300 bg-amber-50 px-2 py-1 text-[11px] font-semibold text-amber-800 transition-colors hover:bg-amber-100"
                          >
                            <Upload className="size-3.5" aria-hidden="true" />
                            Subir documento
                          </button>
                        )}
                        {venta.documento ? (
                          <p className="truncate text-[11px] text-ink-400" title={venta.documento}>
                            {venta.documento}
                          </p>
                        ) : (
                          <p className="text-[11px] text-ink-400">
                            PDF, PNG, JPG, JPEG o WEBP · hasta 5 MB
                          </p>
                        )}
                      </td>
                      <td className="px-1.5 py-2">
                        <button
                          type="button"
                          onClick={() => setDetalleDe(venta.id)}
                          className="inline-flex items-center gap-1 text-[11px] font-semibold text-brand-700 hover:underline"
                          title="Ver el detalle de productos y entregas"
                        >
                          <ListTree className="size-3.5" aria-hidden="true" />
                          Detalle
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
                {ventas.length > 0 ? (
                  <tfoot className="sticky bottom-0 bg-white shadow-[0_-1px_0_0_var(--color-ink-100)]">
                    <tr className="text-[13px] font-semibold text-ink-800">
                      <td className="px-1.5 py-2" colSpan={4}>
                        TOTAL · {formatNumber(resumen?.ventas ?? 0)} factura(s)
                      </td>
                      <td className="px-1.5 py-2 text-right">
                        {formatCurrency(resumen?.total ?? 0)}
                      </td>
                      <td className="px-1.5 py-2" colSpan={2}>
                        <span className="text-[11px] font-normal text-ink-500">
                          {formatNumber(resumen?.con_documento ?? 0)} con documento ·{' '}
                          {formatNumber(resumen?.sin_documento ?? 0)} sin documento
                        </span>
                      </td>
                    </tr>
                  </tfoot>
                ) : null}
              </table>
            </div>

            <Paginador {...paginacion} etiqueta="facturas" />
            </>
          ) : null}
        </CardContent>
      </Card>

      <Dialog
        open={detalleDe !== null}
        onClose={() => setDetalleDe(null)}
        title="Detalle de la factura"
        description={ventaDelDetalle?.comprobante ?? undefined}
        size="lg"
      >
        <div className="space-y-4">
          {detalle.isLoading ? <Skeleton className="h-40 w-full" /> : null}

          {!detalle.isLoading ? (
            <>
              <div>
                <h3 className="mb-2 text-sm font-semibold text-ink-800">Factura global</h3>
                <div className="overflow-hidden rounded-card border border-ink-100">
                  <table className="w-full border-collapse text-[13px]">
                    <thead className="bg-ink-50/60">
                      <tr className="text-left text-[11px] font-semibold tracking-wide text-ink-500 uppercase">
                        <th className="px-2 py-2">Producto</th>
                        <th className="w-[4.5rem] px-2 py-2 text-right">Comprada</th>
                        <th className="w-[4.5rem] px-2 py-2 text-right">Entregada</th>
                        <th className="w-[4.5rem] px-2 py-2 text-right">Pendiente</th>
                        <th className="w-[5.5rem] px-2 py-2 text-right">Total</th>
                      </tr>
                    </thead>
                    <tbody>
                      {(detalle.data?.productos ?? []).map((fila) => (
                        <tr key={fila.producto} className="border-t border-ink-100">
                          <td className="px-2 py-2 font-semibold text-ink-800">{fila.producto}</td>
                          <td className="px-2 py-2 text-right text-ink-700">
                            {formatNumber(fila.comprado)}
                          </td>
                          <td className="px-2 py-2 text-right text-ink-700">
                            {formatNumber(fila.entregado)}
                          </td>
                          <td
                            className={cn(
                              'px-2 py-2 text-right font-semibold',
                              fila.pendiente > 0 ? 'text-amber-700' : 'text-emerald-700',
                            )}
                          >
                            {formatNumber(fila.pendiente)}
                          </td>
                          <td className="px-2 py-2 text-right text-ink-900">
                            {formatCurrency(fila.total)}
                          </td>
                        </tr>
                      ))}
                      {(detalle.data?.productos ?? []).length === 0 ? (
                        <tr className="border-t border-ink-100">
                          <td colSpan={5} className="px-2 py-6 text-center text-sm text-ink-500">
                            La factura no tiene productos.
                          </td>
                        </tr>
                      ) : null}
                    </tbody>
                  </table>
                </div>
              </div>

              <div>
                <h3 className="mb-2 text-sm font-semibold text-ink-800">
                  Entregas ({(detalle.data?.entregas ?? []).length})
                </h3>
                <div className="max-h-[30vh] overflow-y-auto rounded-card border border-ink-100">
                  <table className="w-full border-collapse text-[13px]">
                    <thead className="sticky top-0 bg-ink-50/60">
                      <tr className="text-left text-[11px] font-semibold tracking-wide text-ink-500 uppercase">
                        <th className="px-2 py-2">Fecha</th>
                        <th className="px-2 py-2">Producto</th>
                        <th className="w-[4.5rem] px-2 py-2 text-right">Cantidad</th>
                        <th className="w-[7rem] px-2 py-2">Comprobante</th>
                      </tr>
                    </thead>
                    <tbody>
                      {(detalle.data?.entregas ?? []).map((fila) => (
                        <tr key={`${fila.nota_venta_id}-${fila.comprobante}`} className="border-t border-ink-100">
                          <td className="px-2 py-2 text-ink-700">{formatDateValue(fila.fecha)}</td>
                          <td className="px-2 py-2 text-ink-700">{fila.producto ?? '—'}</td>
                          <td className="px-2 py-2 text-right text-ink-700">
                            {formatNumber(fila.cantidad)}
                          </td>
                          <td className="px-2 py-2 font-semibold text-ink-800">
                            {fila.comprobante}
                          </td>
                        </tr>
                      ))}
                      {(detalle.data?.entregas ?? []).length === 0 ? (
                        <tr className="border-t border-ink-100">
                          <td colSpan={4} className="px-2 py-6 text-center text-sm text-ink-500">
                            Todavía no hay entregas registradas.
                          </td>
                        </tr>
                      ) : null}
                    </tbody>
                  </table>
                </div>
              </div>

              {detalle.data?.documento ? (
                <div className="flex items-center justify-between gap-2 rounded-card border border-ink-100 bg-ink-50/40 px-3 py-2">
                  <span className="truncate text-xs text-ink-600" title={detalle.data.documento}>
                    <FileText className="mr-1 inline size-3.5" aria-hidden="true" />
                    {detalle.data.documento}
                  </span>
                  {ventaDelDetalle ? (
                    <Button variant="outline" onClick={() => void verDocumento(ventaDelDetalle)}>
                      Ver documento
                    </Button>
                  ) : null}
                </div>
              ) : null}
            </>
          ) : null}
        </div>
      </Dialog>

      <p className="flex items-start gap-2 text-xs text-ink-500">
        <Info className="mt-0.5 size-3.5 shrink-0" aria-hidden="true" />
        <span>
          El documento de pago se guarda en <code>uploads/pagos_adelantados/</code> y su nombre en la
          factura: al reemplazarlo se borra el archivo anterior, como en el panel anterior. El
          «pendiente de entrega» es lo comprado menos lo entregado con notas de venta.
        </span>
      </p>
    </div>
  );
}
